

import { useState, useCallback, useMemo } from 'react';
import { Modal, Box, Text, Stack, ActionIcon, Group, Button, Tooltip } from '@mantine/core';
import { IconX, IconEye, IconSparkles } from '@tabler/icons-react';
import { Node } from 'reactflow';
import { PageElement, PageNode } from '@holloway/dsl';
import { useQuery } from '@tanstack/react-query';
import { variablesApi } from '@/services/variables.api';
import { toPickerItems } from '@/features/editor/utils/variables';
import { usePageEditorState } from './hooks/usePageEditorState';
import { useElementDragAndDrop } from './hooks/useElementDragAndDrop';
import PageElementPalette from './PageElementPalette';
import PageElementsCanvas from './PageEditor/PageElementsCanvas';
import PageElementProperties from './PageElements/PageElementProperties';
import { renderElementContent } from './PageElements/Renderers';

interface Props {
  opened: boolean;
  onClose: () => void;
  pageNode: Partial<PageNode>;
  onSave: (updatedNode: Partial<PageNode>) => void;
  nodes?: Node[];
  onAddNode?: (type: string) => string;
  onSaveFlow?: () => void;
  flowId?: string;
}

export default function PageEditor({ opened, onClose, pageNode, onSave, nodes, onAddNode, onSaveFlow, flowId }: Props) {
  const [canvasWidth, setCanvasWidth] = useState(340);
  const [isResizing, setIsResizing] = useState(false);
  const [previewOpened, setPreviewOpened] = useState(false);

  const { data: apiVariables = [] } = useQuery({
    queryKey: ['variables', 'flow', flowId],
    queryFn: () => flowId ? variablesApi.getFlowVariables(flowId) : Promise.resolve([]),
    enabled: !!flowId,
  });

  // Map page node ids -> current title so page-scoped variables render as
  // "PageTitle.varName" (resolved live, so renames are always reflected).
  const pageTitleById = useMemo(() => {
    const map = new Map<string, string>();
    (nodes || []).forEach((n) => {
      if (n.type === 'page') map.set(n.id, n.data?.title || n.id);
    });
    return map;
  }, [nodes]);

  const editorVariables = useMemo(
    () => toPickerItems(apiVariables, pageTitleById),
    [apiVariables, pageTitleById],
  );

  const {
    editingNode,
    setEditingNode,
    selectedElementId,
    setSelectedElementId,
    selectedElement,
    handleAddElement,
    handleRemoveElement,
    handleUpdateElement,
    handleReorderElements,
    handleSave,
  } = usePageEditorState({
    pageNode,
    opened,
    onSave,
    onClose,
    onSaveFlow,
  });

  const { sensors, handleDragEnd } = useElementDragAndDrop({
    elements: editingNode.elements || [],
    onReorder: handleReorderElements,
  });

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);

    const startX = e.clientX;
    const startWidth = canvasWidth;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - startX;
      const newWidth = Math.max(200, Math.min(600, startWidth + delta));
      setCanvasWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [canvasWidth]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="95%"
      padding={0}
      styles={{
        body: { height: '90vh', display: 'flex', flexDirection: 'column' },
        content: { backgroundColor: '#F2F4F7', borderRadius: 12 },
      }}
    >
      {/* Header Bar */}
      <Box
        px="lg"
        py="sm"
        style={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E4E7ED',
          borderRadius: '12px 12px 0 0',
          flexShrink: 0,
        }}
      >
        <Group justify="space-between">
          <Group gap="sm">
            <Box
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: '#415A91',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconSparkles size={18} color="white" />
            </Box>
            <Box>
              <Text fw={700} size="md">{editingNode.title || 'Page Editor'}</Text>
              <Text size="xs" c="dimmed">ScreenID: {editingNode.id || 'new'}</Text>
            </Box>
          </Group>
          <Group gap="xs">
            <Tooltip label="Preview">
              <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Preview" onClick={() => setPreviewOpened(true)}>
                <IconEye size={20} />
              </ActionIcon>
            </Tooltip>
            <Button
              onClick={handleSave}
              color="#415A91"
              radius="md"
              size="sm"
            >
              Update Page
            </Button>
            <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Close page editor" onClick={onClose}>
              <IconX size={20} />
            </ActionIcon>
          </Group>
        </Group>
      </Box>

      {/* Main Content */}
      <Box style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Left Panel - Components Library */}
        <Box
          style={{
            width: 220,
            flexShrink: 0,
            backgroundColor: '#F2F4F7',
            borderRight: '1px solid #E4E7ED',
            overflowY: 'auto',
            padding: '16px 12px',
          }}
        >
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="md" style={{ letterSpacing: '0.05em' }}>
            Components Library
          </Text>
          <PageElementPalette onAdd={handleAddElement} />
        </Box>

        {/* Center - Canvas (resizable) */}
        <Box style={{ width: canvasWidth, flexShrink: 0, overflowY: 'auto', padding: '16px', backgroundColor: '#F2F4F7' }}>
          <PageElementsCanvas
            elements={editingNode.elements || []}
            variables={editorVariables}
            selectedElementId={selectedElementId}
            sensors={sensors}
            onSelect={setSelectedElementId}
            onRemove={handleRemoveElement}
            onUpdate={handleUpdateElement}
            onDragEnd={handleDragEnd}
          />
        </Box>

        {/* Resizable Divider */}
        <Box
          onMouseDown={handleMouseDown}
          style={{
            width: 6,
            flexShrink: 0,
            cursor: 'col-resize',
            backgroundColor: isResizing ? '#415A91' : '#E4E7ED',
            transition: isResizing ? 'none' : 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = '#BCC3CF';
          }}
          onMouseLeave={(e) => {
            if (!isResizing) e.currentTarget.style.backgroundColor = '#E4E7ED';
          }}
        >
          <Box
            style={{
              width: 2,
              height: 40,
              backgroundColor: isResizing ? '#fff' : '#8E99AB',
              borderRadius: 2,
            }}
          />
        </Box>

        {/* Right Panel - Component Settings */}
        <Box
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            backgroundColor: '#FFFFFF',
          }}
        >
          {selectedElement ? (
            <Stack gap="md">
              <Text fw={700} size="md">Component Settings</Text>
              {/* Keyed by element so switching elements mounts a fresh panel. The
                  rich text editor reads its content only when created, so a reused
                  panel kept showing the previous element's text and saved it over
                  the newly selected element on the first keystroke. */}
              <PageElementProperties
                key={selectedElement.id}
                element={selectedElement}
                onChange={(updates) => handleUpdateElement(selectedElement.id, updates)}
                onRemove={() => {
                  handleRemoveElement(selectedElement.id);
                  setSelectedElementId(null);
                }}
                nodes={nodes}
                onAddNode={onAddNode}
                variables={editorVariables}
                rawVariables={apiVariables}
                flowId={flowId}
                pageNodeId={editingNode.id}
                pageElements={editingNode.elements || []}
              />
            </Stack>
          ) : (
            <Box
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text c="dimmed" size="sm">
                Select a component to edit its settings
              </Text>
            </Box>
          )}
        </Box>
      </Box>

      <Modal
        opened={previewOpened}
        onClose={() => setPreviewOpened(false)}
        title={editingNode.title || 'Page Preview'}
        size="md"
        centered
      >
        <Stack gap="md">
          {(editingNode.elements || []).length === 0 ? (
            <Text c="dimmed" size="sm">No components to preview.</Text>
          ) : (
            (editingNode.elements || []).map((element) => (
              <Box key={element.id}>
                {renderElementContent(element as PageElement, undefined, editorVariables, { fullPreview: true })}
              </Box>
            ))
          )}
        </Stack>
      </Modal>
    </Modal>
  );
}
